import { forwardRef, type CSSProperties, type HTMLAttributes } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

export type GridProps = HTMLAttributes<HTMLDivElement> & {
  columns?: 1 | 2 | 3 | 4 | string;
  templateColumns?: string;
  minColumnWidth?: string;
  gap?: "sm" | "md" | "lg";
};

interface GridOwnerState {
  columns: 1 | 2 | 3 | 4 | null;
  gap: NonNullable<GridProps["gap"]>;
}

/* Exact compiled output of the pre-migration Tailwind utilities
 * (grid → responsive columns → gap), verified against the frozen baseline.
 * Custom templates / minColumnWidth keep their inline-style treatment (the
 * column classes were skipped in that case, exactly as before). */

const gridColumnsStyles: Record<1 | 2 | 3 | 4, CSSObject> = {
  1: { gridTemplateColumns: "repeat(1,minmax(0,1fr))" },
  2: {
    gridTemplateColumns: "repeat(1,minmax(0,1fr))",
    "@media (min-width:768px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" }
  },
  3: {
    gridTemplateColumns: "repeat(1,minmax(0,1fr))",
    "@media (min-width:768px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
    "@media (min-width:1280px)": { gridTemplateColumns: "repeat(3,minmax(0,1fr))" }
  },
  4: {
    gridTemplateColumns: "repeat(1,minmax(0,1fr))",
    "@media (min-width:640px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" },
    "@media (min-width:1280px)": { gridTemplateColumns: "repeat(4,minmax(0,1fr))" }
  }
};

const gridGapStyles: Record<GridOwnerState["gap"], CSSObject> = {
  sm: { gap: ".75rem" },
  md: { gap: "1.25rem" },
  lg: { gap: "1.5rem" }
};

const GridRoot = styled("div", {
  label: "AgenticGrid-root",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: GridOwnerState }>(({ ownerState }) => [
  { display: "grid" },
  ownerState.columns !== null && gridColumnsStyles[ownerState.columns],
  gridGapStyles[ownerState.gap]
]);

export const Grid = forwardRef<HTMLDivElement, GridProps>(({ className, columns = 3, templateColumns, minColumnWidth, gap = "md", style, ...props }, ref) => {
  const customTemplate = templateColumns ?? (typeof columns === "string" ? columns : undefined);
  const gridStyle: CSSProperties = {
    ...(customTemplate ? { gridTemplateColumns: customTemplate } : {}),
    ...(minColumnWidth ? { gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))` } : {}),
    ...style
  };

  return (
    <GridRoot
      ref={ref}
      ownerState={{
        columns: typeof columns === "number" && !customTemplate && !minColumnWidth ? columns : null,
        gap
      }}
      className={className}
      style={gridStyle}
      {...props}
    />
  );
});

Grid.displayName = "Grid";
